import React from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { StyleSheet, Text, View } from './components/base_components';
import { useBusy } from './components/busy_component';
import BreadcrumbPath from './components/breadcrumb_path';
import DownloadProgress from './components/download_progress';
import ProgressSpinner from './components/progress_spinner';
import StandardBoxList from './components/standard_box_list';
import TextButton from './components/buttons/text_button';
import TitleActionLine from './components/title_action_line';

import DownloadHelper from './lib/download_helper';
import VolumeDirHelper from './lib/volume_dir_helper';

import MediaGrantStore from './stores/media_grant_store';
import UserStore from './stores/user_store';

import { byteFormat } from './tools/util';

const styles = StyleSheet.create({
  downloadScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  actionButton: {
    marginLeft: '1rem',
  },
  spinner: {
    margin: '1.5rem',
    alignSelf: 'center',
  },
  text: {
    marginTop: '2rem',
    alignSelf: 'center',
    color: '#18182d',
    fontSize: '1.5rem',
    fontWeight: '400',
  },
});

export default function DownloadScreen(props) {
  const params = useParams();
  const location = useLocation();
  const org = UserStore.useOrg();
  const grant_name = params.grant_name ?? '';
  const path = '/' + (params['*'] ?? '');
  const grant = MediaGrantStore.useMediaGrant(org?.org_id, grant_name);
  const media_grant_id = grant?.media_grant_id;
  const [list, setList] = React.useState<any[]>([]);
  const [is_ready, setIsReady] = React.useState(false);
  const [is_busy, setBusy, clearBusy] = useBusy();

  React.useEffect(() => {
    if (location?.state && media_grant_id && org) {
      const { media_list, dir_list } = location.state;
      VolumeDirHelper.fetch(
        { org_id: org?.org_id, media_grant_id, path, media_list, dir_list },
        (err, found_list) => {
          if (err) {
            window.alert('Failed to collect files, please try again.');
          } else {
            setIsReady(true);
            setList(found_list);
          }
        }
      );
    }
  }, [org, media_grant_id, path, location?.state]);

  function _onDownloadPress() {
    if (list?.length > 0 && setBusy()) {
      _onRetry();
    }
  }
  function _onRetry() {
    const opts = { org_id: org?.org_id, media_grant_id, list, path };
    DownloadHelper.run(opts, (err) => {
      if (err === 'not_supported') {
        window.alert(
          'Looks like your browser does not suport multiple file downloads.'
        );
        clearBusy();
      } else if (err?.name === 'AbortError') {
        clearBusy();
        // user abort
      } else if (err) {
        window.alert('Failed to start download, please try again.');
        clearBusy();
      }
    });
  }
  let rows: any[] = [];
  let content: any = null;
  let total_size = 0;
  let buttons: any = null;
  if (is_busy) {
    content = (
      <DownloadProgress mediaGrant={grant} path={path} onRetry={_onRetry} />
    );
  } else if (is_ready) {
    const prefix_len = path.length;
    rows = list.map((file) => {
      total_size += file.file_size;
      return [file.media_path.slice(prefix_len), byteFormat(file.file_size)];
    });
    buttons = (
      <>
        <TextButton
          style={styles.actionButton}
          text='Download All'
          onPress={_onDownloadPress}
        />
      </>
    );
    content = (
      <StandardBoxList
        title='Download Files'
        detail={rows ? `${rows.length} Files, ${byteFormat(total_size)}` : ''}
        buttons={buttons}
        headers={['Path', 'Size']}
        rows={rows}
        sizes={[3]}
      />
    );
  } else {
    content = (
      <>
        <TitleActionLine title='Download Files' />
        <Text style={styles.text}>Collecting directory entries...</Text>
        <ProgressSpinner style={styles.spinner} color='#999' />
      </>
    );
  }

  return location.state ? (
    <View style={styles.downloadScreen}>
      <BreadcrumbPath
        volumeName={grant?.grant_name}
        prefix={`/home/${grant?.grant_name}/`}
        path={path}
        extra='Download'
      />
      {content}
    </View>
  ) : (
    <Navigate to={`/home/${grant_name}${path}`} />
  );
}
